// Auto-recovery for the production ErrorBoundary flow (see error.page.tsx).
//
// Strategy: when an error is caught we do ONE silent reload. If the error comes
// back within RELOAD_WINDOW_MS, the reload clearly didn't help, so we stop and
// redirect the user Home instead of reloading again.
//
// A timestamp (not a mount-based flag) is used on purpose: the error can be
// thrown *after* a successful mount (effects, lazy routes, interactions), so
// "the app mounted" is NOT proof the error is gone - relying on that caused an
// infinite reload loop. A time window is self-resetting and loop-proof.

const RELOAD_WINDOW_MS = 20_000

const LAST_RELOAD_KEY = 'app-error-last-reload'

// Returns sessionStorage only if it is actually usable. If it throws (disabled
// in some privacy modes) we must know, because without persistence we can never
// count reloads and would loop forever - callers fall back to not reloading.
const getStorage = (): Storage | null => {
  try {
    const storage = window.sessionStorage
    const probe = '__err_probe__'
    storage.setItem(probe, '1')
    storage.removeItem(probe)
    return storage
  } catch {
    return null
  }
}

export const canPersistErrorReload = (): boolean => getStorage() !== null

/** True if a silent error-reload happened very recently (i.e. it didn't help). */
export const isRecentErrorReload = (): boolean => {
  const storage = getStorage()
  if (!storage) return false
  const last = Number(storage.getItem(LAST_RELOAD_KEY))
  if (!last) return false
  return Date.now() - last < RELOAD_WINDOW_MS
}

/** Records that we are about to do a silent error-reload. */
export const markErrorReload = (): void => {
  const storage = getStorage()
  try {
    storage?.setItem(LAST_RELOAD_KEY, String(Date.now()))
  } catch {
    /* ignore */
  }
}

export const clearErrorReload = (): void => {
  const storage = getStorage()
  try {
    storage?.removeItem(LAST_RELOAD_KEY)
  } catch {
    /* ignore */
  }
}
